import React from 'react';
import { Radio, RadioGroup } from '@this/src/components/shared/ui/inputs/radio';
import type { PickupStationJson, PickupStationOption, PickupStationParam } from '../../../api/reservationInfo';

const PICKUP_SERVICE_EKINET = 0;
const PICKUP_SERVICE_E5489 = 1;

type Props = {
  options: PickupStationOption[];
  stations: PickupStationJson[];
  pickupStations: PickupStationParam[];
  error: string | null;
  disabled: boolean;
  onChange: (pickupStations: PickupStationParam[]) => void;
};

const getTicketingQrServiceLabel = (ticketingQrService: number): string => {
  switch (ticketingQrService) {
    case PICKUP_SERVICE_EKINET:
      return 'JR東日本';
    case PICKUP_SERVICE_E5489:
      return 'JR西日本';
    default:
      return '';
  }
};

const getTicketingMethodFromService = (pickupService: number): string => {
  switch (pickupService) {
    case PICKUP_SERVICE_EKINET:
      return 'qr_east';
    case PICKUP_SERVICE_E5489:
      return 'qr_west';
    default:
      return '';
  }
};

const ReservationInfoPickupStationSection = ({
  options,
  stations,
  pickupStations,
  error,
  disabled,
  onChange
}: Props) => {
  const styles = getStyles();

  const stripParenthesisSuffix = (name: string): string => name.replace(/[（(][^）)]*[）)]$/, '');

  const stationNameCandidates = (stationName: string): string[] => {
    const withoutEki = stationName.replace(/駅$/, '');
    const stripped = stripParenthesisSuffix(withoutEki);
    return Array.from(
      new Set([stationName, withoutEki, `${withoutEki}駅`, stripped, `${stripped}駅`].filter(Boolean))
    );
  };

  const findStationByName = (stationName: string): PickupStationJson | undefined => {
    const nameCandidates = stationNameCandidates(stationName);
    let matched = stations.filter(
      s => nameCandidates.includes(s.stationName) || nameCandidates.includes(s.stationName.replace(/駅$/, ''))
    );
    if (matched.length === 0) {
      return undefined;
    }

    // 「郡山(福島県)」→ 福島県の郡山だけに絞る（奈良県の郡山へフォールバックしない）
    const prefHintMatch = stationName.replace(/駅$/, '').match(/[（(]([^）)]+)[）)]$/);
    if (prefHintMatch) {
      const hint = prefHintMatch[1].replace(/[都道府県]$/, '');
      const byPref = matched.filter(s => {
        const prefName = s.prefecture?.name || '';
        return prefName.includes(hint) || hint.includes(prefName.replace(/[都道府県]$/, ''));
      });
      if (byPref.length === 0) {
        return undefined;
      }
      matched = byPref;
    }

    return matched[0];
  };

  const getStationDisplayName = (stationName: string): string => {
    const station = findStationByName(stationName);
    const rawBase = station?.stationName || stationName;
    const baseName = rawBase.endsWith('駅') ? rawBase : `${rawBase}駅`;
    const railwayArea = station?.prefecture?.railwayAreas?.[0];
    if (railwayArea) {
      const serviceLabel = getTicketingQrServiceLabel(railwayArea.ticketingQrService);
      if (serviceLabel) {
        return `${baseName}（${serviceLabel}）`;
      }
    }
    return baseName;
  };

  const getSelectionValue = (searchQueryItemId: number): string => {
    const current = pickupStations.find(pickup => pickup.searchQueryItemId === searchQueryItemId);
    if (!current) return '';
    if (current.pickupStation === 'その他') return 'other';
    return current.pickupStation.replace(/駅$/, '');
  };

  const getPickupService = (searchQueryItemId: number): number | null => {
    const current = pickupStations.find(pickup => pickup.searchQueryItemId === searchQueryItemId);
    return current?.pickupService ?? null;
  };

  const upsertPickup = (next: PickupStationParam) => {
    const others = pickupStations.filter(pickup => pickup.searchQueryItemId !== next.searchQueryItemId);
    onChange([...others, next]);
  };

  const handleStationChange = (
    option: PickupStationOption,
    value: string,
    stationName: string | null,
    manualService: number | null = null
  ) => {
    const station = stationName ? findStationByName(stationName) : null;
    let ticketingMethod: string | null = null;
    let pickupService: number | null = manualService;
    const railwayArea = station?.prefecture?.railwayAreas?.[0];
    if (railwayArea) {
      pickupService = railwayArea.ticketingQrService;
      ticketingMethod = getTicketingMethodFromService(railwayArea.ticketingQrService);
    } else if (manualService !== null) {
      ticketingMethod = getTicketingMethodFromService(manualService);
    }

    const resolvedStationName = station
      ? station.stationName.endsWith('駅')
        ? station.stationName
        : `${station.stationName}駅`
      : stationName
      ? stationName.endsWith('駅')
        ? stationName
        : `${stationName}駅`
      : null;

    upsertPickup({
      searchQueryItemId: option.searchQueryItemId,
      pickupStation: value === 'other' ? 'その他' : resolvedStationName || '',
      pickupStationId: station?.id || null,
      pickupService,
      ticketingMethod
    });
  };

  if (options.length === 0) {
    return null;
  }

  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>チケット受け取り希望場所について</div>
      {options.map(option => {
        const selectedValue = getSelectionValue(option.searchQueryItemId);
        const isOtherSelected = selectedValue === 'other';
        return (
          <div key={option.searchQueryItemId} style={styles.itemSection}>
            <div style={styles.routeLabel}>
              {option.fromName} → {option.toName}
            </div>
            <RadioGroup
              layout="vertical"
              value={selectedValue || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value === 'other') {
                  handleStationChange(option, value, null);
                } else {
                  handleStationChange(option, value, value);
                }
              }}
            >
              {option.departureStations.map(stationName => (
                <Radio key={stationName} value={stationName} disabled={disabled}>
                  {getStationDisplayName(stationName)}
                </Radio>
              ))}
              <Radio value="other" disabled={disabled}>
                その他
              </Radio>
            </RadioGroup>
            {isOtherSelected && (
              <div style={styles.serviceSection}>
                <div style={styles.serviceLabel}>発券サービスを選択してください</div>
                <RadioGroup
                  layout="vertical"
                  value={getPickupService(option.searchQueryItemId)?.toString() || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const service = parseInt(e.target.value, 10);
                    handleStationChange(option, 'other', null, service);
                  }}
                >
                  <Radio value={PICKUP_SERVICE_EKINET.toString()} disabled={disabled}>
                    JR東日本（えきねっと）
                  </Radio>
                  <Radio value={PICKUP_SERVICE_E5489.toString()} disabled={disabled}>
                    JR西日本（e5489）
                  </Radio>
                </RadioGroup>
              </div>
            )}
          </div>
        );
      })}
      {error && <span style={styles.errorText}>{error}</span>}
      <div style={styles.notice}>
        <div>【注意事項】</div>
        <ul style={styles.noticeList}>
          <li>指定した受け取り駅が所属するJRサービスであればお受け取り可能です。</li>
          <li>選択した受け取り場所は、ご指定後に変更することはできません。</li>
        </ul>
      </div>
    </div>
  );
};

const getStyles = (): Record<string, React.CSSProperties> => ({
  section: {
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '12px',
    color: '#333'
  },
  itemSection: {
    marginBottom: '12px'
  },
  routeLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333'
  },
  serviceSection: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#f8f8f8',
    borderRadius: '4px'
  },
  serviceLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '6px',
    color: '#333'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  notice: {
    fontSize: '11px',
    lineHeight: '16px',
    color: '#555',
    marginTop: '8px'
  },
  noticeList: {
    margin: '4px 0 0',
    paddingLeft: '16px'
  }
});

export default ReservationInfoPickupStationSection;
